import type { Locale } from '@/i18n/locales'
import { DEFAULT_LOCALE, LOCALES, isLocale } from '@/i18n/locales'

export const ROUTE_KINDS = [
  'home',
  'privacy',
  'legal',
  'mediusPrivacy',
  'mediusDeletion',
  'mediusTerms',
  'sparsaPrivacy',
  'sparsaDeletion',
  'sparsaTerms',
  'sparsaAcceptableUse',
] as const

export type RouteKind = (typeof ROUTE_KINDS)[number]

export interface Route {
  kind: RouteKind | 'notFound'
  locale: Locale
}

const PRIVACY_SLUGS: Record<Locale, string> = {
  hr: 'privatnost',
  en: 'privacy',
  de: 'datenschutz',
  it: 'privacy',
  sr: 'privatnost',
  sl: 'zasebnost',
  tr: 'gizlilik',
}

const LEGAL_SLUGS: Record<Locale, string> = {
  hr: 'impresum',
  en: 'legal-notice',
  de: 'impressum',
  it: 'note-legali',
  sr: 'impresum',
  sl: 'pravno-obvestilo',
  tr: 'yasal-bilgiler',
}

const MEDIUS_PRIVACY_SLUGS: Record<Locale, string> = {
  hr: 'medius/privatnost',
  en: 'medius/privacy',
  de: 'medius/datenschutz',
  it: 'medius/privacy',
  sr: 'medius/privatnost',
  sl: 'medius/zasebnost',
  tr: 'medius/gizlilik',
}

const MEDIUS_DELETION_SLUGS: Record<Locale, string> = {
  hr: 'medius/brisanje-podataka',
  en: 'medius/data-deletion',
  de: 'medius/datenloeschung',
  it: 'medius/cancellazione-dati',
  sr: 'medius/brisanje-podataka',
  sl: 'medius/izbris-podatkov',
  tr: 'medius/veri-silme',
}

const MEDIUS_TERMS_SLUGS: Record<Locale, string> = {
  hr: 'medius/uvjeti-koristenja',
  en: 'medius/terms',
  de: 'medius/nutzungsbedingungen',
  it: 'medius/termini',
  sr: 'medius/uslovi-koriscenja',
  sl: 'medius/pogoji-uporabe',
  tr: 'medius/kullanim-kosullari',
}

const SPARSA_PRIVACY_SLUGS: Record<Locale, string> = {
  hr: 'sparsa/privatnost',
  en: 'sparsa/privacy',
  de: 'sparsa/datenschutz',
  it: 'sparsa/privacy',
  sr: 'sparsa/privatnost',
  sl: 'sparsa/zasebnost',
  tr: 'sparsa/gizlilik',
}

const SPARSA_DELETION_SLUGS: Record<Locale, string> = {
  hr: 'sparsa/brisanje-podataka',
  en: 'sparsa/data-deletion',
  de: 'sparsa/datenloeschung',
  it: 'sparsa/cancellazione-dati',
  sr: 'sparsa/brisanje-podataka',
  sl: 'sparsa/izbris-podatkov',
  tr: 'sparsa/veri-silme',
}

const SPARSA_TERMS_SLUGS: Record<Locale, string> = {
  hr: 'sparsa/uvjeti-koristenja',
  en: 'sparsa/terms',
  de: 'sparsa/nutzungsbedingungen',
  it: 'sparsa/termini',
  sr: 'sparsa/uslovi-koriscenja',
  sl: 'sparsa/pogoji-uporabe',
  tr: 'sparsa/kullanim-kosullari',
}

const SPARSA_ACCEPTABLE_USE_SLUGS: Record<Locale, string> = {
  hr: 'sparsa/prihvatljivo-koristenje',
  en: 'sparsa/acceptable-use',
  de: 'sparsa/zulaessige-nutzung',
  it: 'sparsa/uso-accettabile',
  sr: 'sparsa/prihvatljivo-koriscenje',
  sl: 'sparsa/sprejemljiva-uporaba',
  tr: 'sparsa/kabul-edilebilir-kullanim',
}

const SLUGS: Record<Exclude<RouteKind, 'home'>, Record<Locale, string>> = {
  privacy: PRIVACY_SLUGS,
  legal: LEGAL_SLUGS,
  mediusPrivacy: MEDIUS_PRIVACY_SLUGS,
  mediusDeletion: MEDIUS_DELETION_SLUGS,
  mediusTerms: MEDIUS_TERMS_SLUGS,
  sparsaPrivacy: SPARSA_PRIVACY_SLUGS,
  sparsaDeletion: SPARSA_DELETION_SLUGS,
  sparsaTerms: SPARSA_TERMS_SLUGS,
  sparsaAcceptableUse: SPARSA_ACCEPTABLE_USE_SLUGS,
}

export const routePath = (kind: RouteKind, locale: Locale): string => {
  const prefix = locale === DEFAULT_LOCALE ? '' : `/${locale}`
  if (kind === 'home') return prefix === '' ? '/' : `${prefix}/`
  return `${prefix}/${SLUGS[kind][locale]}/`
}

export const allRoutes = (): readonly { kind: RouteKind; locale: Locale; path: string }[] =>
  LOCALES.flatMap((locale) =>
    ROUTE_KINDS.map((kind) => ({ kind, locale, path: routePath(kind, locale) })),
  )

export const PRODUCT_POLICY_KINDS: readonly RouteKind[] = [
  'mediusPrivacy',
  'mediusDeletion',
  'mediusTerms',
  'sparsaPrivacy',
  'sparsaDeletion',
  'sparsaTerms',
  'sparsaAcceptableUse',
]

export const sitemapRoutes = (): readonly { kind: RouteKind; locale: Locale; path: string }[] =>
  allRoutes().filter((route) => !PRODUCT_POLICY_KINDS.includes(route.kind))

const segmentsOf = (pathname: string): string[] =>
  pathname.split('/').filter((segment) => segment.length > 0)

export const matchRoute = (pathname: string): Route => {
  const segments = segmentsOf(pathname)
  const first = segments[0]
  const hasLocalePrefix = first !== undefined && isLocale(first)
  const locale: Locale = hasLocalePrefix && first !== undefined ? first : DEFAULT_LOCALE
  const rest = hasLocalePrefix ? segments.slice(1) : segments

  if (rest.length === 0) return { kind: 'home', locale }
  const slug = rest.join('/')
  for (const kind of ROUTE_KINDS) {
    if (kind !== 'home' && SLUGS[kind][locale] === slug) return { kind, locale }
  }

  return { kind: 'notFound', locale }
}

export const privacySlug = (locale: Locale): string => PRIVACY_SLUGS[locale]
